import { Stack, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import { useFormik } from 'formik'
import * as React from 'react'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInvalidateCollaborationAppMeetingQuery } from '../../api/hooks/useInvalidateCollaborationAppMeetingQuery'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { useAssistantAdministrationStore } from '../../store/storeAssistantAdministration'
import { theme } from '../../theme'

type Props = {
    open: boolean
    handleClose: VoidFunction
    idMeeting?: string
    source: string
}

export const MeetEditDialog: React.FC<Props> = ({ open, handleClose, idMeeting, source }) => {
    const [startAt, setStartAt] = React.useState(null)
    const [error, setError] = React.useState(false)
    const [searchParams] = useSearchParams()
    const yesterday = dayjs().subtract(-1, 'year')
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const invalidateMeetings = useInvalidateCollaborationAppMeetingQuery()

    const {
        updateMeeting,
        errorUpdateMeeting,
        isLoadingUpdateMeeting,
        fetchMeeting,
        isUpdateMeetingDone,
    } = useAssistantAdministrationStore((state) => state)

    useEffect(() => {
        if (isUpdateMeetingDone) {
            fetchMeeting({ status__in: searchParams.get('status') })
            invalidateMeetings()
            handleClose()
        }
    }, [isUpdateMeetingDone])

    const handleCloseDialog = () => {
        formik.resetForm()
        setStartAt(null)
        handleClose()
    }

    const formik = useFormik({
        initialValues: {
            source: '',
        },
        onSubmit: async () => {
            await updateMeeting(
                {
                    meetingId: idMeeting,
                },
                { start_at: startAt },
            )
        },
    })

    return (
        <ModalDialogWrapper
            open={open}
            sx={{
                '.MuiPaper-root': {
                    width: '560px',
                    maxWidth: '560px',
                },
            }}
            footerActions={
                <>
                    <Button fullWidth onClick={handleClose} variant="outlined" color="primary">
                        Отменить
                    </Button>
                    <Button
                        fullWidth
                        onClick={() => formik.handleSubmit()}
                        variant="contained"
                        disabled={isLoadingUpdateMeeting || !startAt || error}>
                        Сохранить
                    </Button>
                </>
            }
            handleClose={handleCloseDialog}
            title="Обновить время подключения бота к встрече">
            {isLoadingUpdateMeeting && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            )}
            {errorUpdateMeeting && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography color="error">Возникла ошибка!</Typography>
                </Box>
            )}

            <Stack direction="column" gap={4}>
                <Typography variant="body2" sx={{ marginY: 1 }}>
                    Ссылка: {source}
                </Typography>
                <DateTimePicker
                    sx={{
                        width: '100%',
                    }}
                    onError={(newError) => setError(newError)}
                    dayOfWeekFormatter={(weekday) => `${weekday.format('dd')}.`}
                    format="DD.MM.YYYY - HH:mm"
                    value={startAt}
                    onChange={(value) => {
                        setStartAt(value)
                    }}
                    label="Дата старта собрания"
                    viewRenderers={
                        !isMobile
                            ? {
                                  hours: null,
                                  minutes: null,
                                  seconds: null,
                              }
                            : {
                                  hours: renderTimeViewClock,
                                  minutes: renderTimeViewClock,
                                  seconds: renderTimeViewClock,
                              }
                    }
                    ampm={false}
                    closeOnSelect={false}
                    disablePast={true}
                    maxDate={yesterday}
                    slotProps={{
                        tabs: {
                            sx: {
                                padding: '16px',
                            },
                        },
                    }}
                />
            </Stack>
        </ModalDialogWrapper>
    )
}
